import { useEffect } from "react";
import { useRouteError } from "react-router";
import { useTranslation } from "react-i18next";

import Button from "@/shared/ui/components/button";
import { ErrorIcon } from "@/shared/ui/icons/error-icon";
import { browserAgent } from "@/shared/telemetry";
import { logError } from "@/services/logging/logger";

/**
 * Route-level error boundary.
 *
 * Without this, React Router falls back to its built-in developer error page —
 * a raw stack trace ending in "Hey developer 👋", which customers were seeing
 * on the live payment screen. It also means render errors never reach New
 * Relic, because React Router swallows them before window.onerror fires.
 */
export function RouteError() {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    const err =
      error instanceof Error ? error : new Error(String(error ?? "unknown"));

    logError("route-error", "Unhandled error rendering route", err, {
      pathname: window.location.pathname,
    });

    try {
      browserAgent?.noticeError(err, {
        source: "route-error-boundary",
        pathname: window.location.pathname,
      });
    } catch {
      // Never let error reporting break the error screen.
    }
  }, [error]);

  return (
    <div
      className="bg-white flex items-center justify-center px-4 min-h-screen"
      data-testid="route-error-screen"
    >
      <div className="max-w-md w-full text-center space-y-8">
        <div className="flex justify-center">
          <div className="flex justify-center items-center w-[72px] h-[72px] bg-[#FFF4ED] rounded-full">
            <ErrorIcon />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 leading-tight">
          {t("error.unknown.title")}
        </h1>

        <p className="text-base text-gray-600 leading-relaxed">
          {t("error.unknown.message")}
        </p>

        <div className="pt-4">
          <Button
            variant="primary"
            size="large"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            {t("error.retry")}
          </Button>
        </div>
      </div>
    </div>
  );
}
